<template>
  <div class="sales-base">
    <div class="d-lg-flex justify-content-between mb-4">
      <h4 class="text-semibold fs-14 py-1 px-2">Visão geral de vendas</h4>
      <snow-button
        size="sm"
        variant="borderless"
        :text="hideValues ? 'Mostrar valores' : 'Ocultar valores'"
        :icon="hideValues ? 'ph ph-eye' : 'ph ph-eye-slash'"
        @click="hideValues = !hideValues"
      />
    </div>
    <template v-if="$permissions.hasPermission('visualizar-valores')">
      <div class="row">
        <div class="col-lg-6 mb-5">
          <div class="p-6 bg-4 rounded-16">
            <div class="d-flex justify-content-between gap-8 py-1 mb-2">
              <h4 class="fs-14">Valor total</h4>
              <i class="ph ph-currency-circle-dollar fs-24" />
            </div>
            <span class="text-semibold fs-24" :class="{ blur: hideValues }">
              R$ {{ formatMoney(totalAmount || 0) }}
            </span>
          </div>
        </div>

        <div class="col-lg-6 mb-5">
          <div class="p-6 bg-3 rounded-16">
            <div class="d-flex justify-content-between gap-8 py-1 mb-2">
              <h4 class="fs-14">Quantidade total</h4>
              <i class="ph ph-users fs-24" />
            </div>
            <span class="text-semibold fs-24" :class="{ blur: hideValues }">
              {{ pagination.count || 0 }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <div class="row justify-content-between mb-5 mb-lg-6">
      <div class="col order-1 order-lg-0">
        <div class="d-flex align-items-center text-neutral-800">
          <span class="functional-1 mr-2">
            Total: {{ pagination.count || 0 }}
          </span>
          <bp-button
            size="md"
            color="neutral"
            variant="glyph"
            icon="fa-light fa-refresh"
            @click="refresh"
          />
        </div>
      </div>

      <div class="col-lg-8">
        <div
          class="d-flex align-items-lg-center flex-column flex-lg-row mb-2 gap-3"
        >
          <div class="d-flex align-items-center gap-3 flex-fill">
            <bp-input
              v-model="search"
              size="md"
              placeholder="Nome, e-mail"
              :delay="600"
              @change="dispatch()"
            />
            <bp-date-picker
              v-model="filters.dates"
              class="d-none d-lg-block"
              @input="refresh"
            />
            <bp-button
              color="neutral"
              size="md"
              variant="filled-1"
              icon="fa-regular fa-filter"
              @click="filterModalValue = true"
            />
          </div>

          <bp-date-picker
            v-model="filters.dates"
            class="d-lg-none w-100"
            @input="refresh"
          />

          <bp-export-button
            v-if="$permissions.hasPermission('exportar-vendas')"
            confirmation
            filename="sales"
            ref="exportButton"
            action="sale/getCSV"
            @select="addressDialog.value = true"
          />
        </div>
      </div>
    </div>

    <div class="my-9 text-center" v-if="loading">
      <b-spinner variant="primary" />
    </div>

    <bp-data-table :columns="columns" :data="sales" v-else>
      <template #produto="{value,item}">
        <div>
          <div class="functional-1">
            {{ value }}
          </div>
          <div class="functional-2 text-neutral-400">
            {{ item.product_type }}
          </div>
        </div>
      </template>

      <template #comprador="{item}">
        <div>
          <div class="functional-1">
            {{ item.shopper_name }}
          </div>
          <div class="shopper-email functional-2 text-neutral-400">
            {{ item.shopper_email }}
          </div>
        </div>
      </template>

      <template #cobrança="{item,value}">
        <div class="d-flex align-items-center">
          <span class="functional-1">
            {{ value }}
          </span>

          <bp-tooltip
            v-if="isSmartCard(item)"
            text="Compra recuperada automaticamente"
          >
            <bp-icon
              icon="fa fa-credit-card"
              size="16"
              class="ml-2 text-information-200"
            />
          </bp-tooltip>

          <bp-tooltip
            v-else-if="isAutoRenew(item)"
            text="Renova até o cliente cancelar"
          >
            <bp-icon icon="fa-light fa-infinity" size="16" class="ml-2" />
          </bp-tooltip>
        </div>
      </template>
    </bp-data-table>

    <div class="d-flex justify-content-end mt-5" v-if="sales.length">
      <bp-paginator
        v-show="!loading"
        v-model="currentPage"
        :pagination="pagination"
        @change-page="dispatch"
      />
    </div>

    <responsive-modal
      v-model="filterModalValue"
      title="Filtrar"
      description="Selecione os filtros e aplique"
    >
      <template>
        <component v-if="filterModal" :is="filterModal" v-model="filters" />
        <div
          class="d-flex flex-column flex-lg-row gap-5 justify-content-end mt-7"
        >
          <bp-button
            label="Cancelar"
            color="neutral"
            variant="filled-1"
            @click="filterModalValue = false"
          />
          <bp-button label="Aplicar" @click="applyFilters" />
        </div>
      </template>
    </responsive-modal>

    <responsive-modal v-model="modal.value" size="lg">
      <component
        v-if="modal.component"
        :is="modal.component"
        :item="itemToShow"
        @refresh="refresh"
        @close="closeModal"
      />
    </responsive-modal>

    <bp-dialog
      v-model="addressDialog.value"
      v-bind="addressDialog"
      @action="exportData(undefined)"
      @secondary-action="exportData(true)"
    />
  </div>
</template>

<script>
import {
  SALE_PAY_METHOD_NAME_MAP,
  SALE_STATUS_BADGE_MAP,
  SALE_STATUS_NAME_MAP
} from "@/constants/sales.js";
import { clearObject, formatMoney } from "@/functions/helpers";
import moment from "moment";
import { mapState } from "vuex";

import AddressConfirmation from "@/components/sale/AddressConfirmation.vue";
import ChangeSubscription from "@/components/sale/ChangeSubscription";
import SaleDetail from "@/components/sale/Details";
import ShowBillet from "@/components/sale/ShowBillet";
import TryAgain from "@/components/sale/TryAgain";

export default {
  components: {
    TryAgain,
    SaleDetail,
    ShowBillet,
    ChangeSubscription,
    AddressConfirmation
  },
  data() {
    return {
      filterModalValue: false,
      filterModal: null,
      filters: {
        status: ["paid", "completed"],
        payment_method: [],
        product_id: [],
        dates: ["", ""],
        is_order_bump: undefined,
        is_upsell: undefined,
        current_install: undefined,
        installments: undefined,
        is_trial: undefined,
        has_coupon: ""
      },
      filterComponent: null,
      search: "",
      currentPage: 1,
      loading: false,
      product_type: undefined,
      hideValues: false,

      modal: {
        value: false,
        component: undefined
      },
      itemToShow: {},

      addressDialog: {
        value: false,
        text: "Deseja exportar o arquivo com o endereço do comprador?",
        icon: "fa fa-circle-exclamation",
        buttonText: "Não",
        secondaryButtonText: "Sim"
      }
    };
  },
  computed: {
    ...mapState("sale", ["sales", "totalAmount", "pagination"]),
    ...mapState("integration", ["coproducerProducts"]),
    columns() {
      return [
        {
          name: "ID Transação",
          field: "id",
          transform: v => `BP${v}`,
          size: "0.7fr"
        },
        {
          name: "Produto",
          field: "product_name"
        },
        {
          name: "Comprador",
          size: "1.2fr"
        },
        {
          name: "Data",
          field: "created_at",
          transform: v => new Date(v).toLocaleDateString("pt-BR"),
          size: "0.5fr"
        },
        {
          name: "Pagamento",
          field: "payment_method",
          transform: v => SALE_PAY_METHOD_NAME_MAP[v],
          size: "0.9fr"
        },
        {
          name: "Parcelamento",
          field: "installments",
          size: "0.7fr",
          transform: this.formatInstallments
        },
        {
          name: "Cobrança",
          field: "current_install",
          transform: this.formatCurrentInstallment,
          size: "0.6fr"
        },
        {
          name: "Comissão",
          field: "amount",
          class: { blur: this.hideValues },
          transform: v => {
            if (this.$permissions.hasPermission("visualizar-valores")) {
              return `R$ ${formatMoney(v)}`;
            }
            return "-";
          },
          size: "0.8fr"
        },
        {
          name: "Status",
          field: "status",
          class: this.formatStatusBadge,
          transform: this.formatStatusName,
          size: "0.7fr"
        },
        {
          name: "",
          type: "menu",
          options: this.itemActions
        }
      ];
    }
  },
  methods: {
    formatMoney,

    getFormattedFilters() {
      const filters = {
        ...this.filters,
        product_type: this.product_type
      };

      if (!filters.is_order_bump) delete filters.is_order_bump;
      if (!filters.is_upsell) delete filters.is_upsell;
      if (!filters.is_trial) delete filters.is_trial;

      if (filters.dates[0]) {
        filters.created_at_after = filters.dates[0];
      }
      if (filters.dates[1]) {
        filters.created_at_before = filters.dates[1];
      }
      delete filters.dates;

      if (filters.has_coupon === "") {
        delete filters.has_coupon;
      } else {
        filters.has_coupon = filters.has_coupon === "has-coupon";
      }

      return filters;
    },
    applyFilters() {
      this.filterModalValue = false;
      this.refresh();
    },
    refresh() {
      this.currentPage = 1;
      this.search = "";
      this.dispatch();
    },
    dispatch(offset) {
      this.loading = true;

      const params = {
        offset,
        search: this.search,
        ...this.getFormattedFilters()
      };

      const actions = [this.$store.dispatch("sale/get", clearObject(params))];
      if (offset === undefined) {
        delete params.offset;
        delete params.search;
        actions.push(
          this.$store.dispatch("sale/getTotalSales", clearObject(params))
        );
      }

      Promise.all(actions).finally(() => {
        this.loading = false;
      });
    },
    exportData(address_export = undefined) {
      const filters = { ...this.getFormattedFilters(), address_export };

      this.$refs.exportButton.exportData(filters);
      this.addressDialog.value = false;
    },

    formatInstallments(installments) {
      if (!installments) return "-";
      return installments == 1 ? "à vista" : installments + "x";
    },
    formatCurrentInstallment(_, item) {
      if (item.charge_type === "signature" && item.subscription)
        return (
          item.subscription.current_cycle +
          " de " +
          (this.isAutoRenew(item) ? "" : item.subscription.cycles)
        );

      if (["card", "two_card"].includes(item.payment_method)) return "1 de 1";

      if (!item.current_install || !item.installments) return "-";

      return item.current_install + " de " + item.installments;
    },
    formatStatusBadge(status) {
      return SALE_STATUS_BADGE_MAP[status];
    },
    formatStatusName(status) {
      return SALE_STATUS_NAME_MAP[status];
    },

    showBillet(item) {
      this.modal.component = ShowBillet;
      this.itemToShow = item;
      this.modal.value = true;
    },
    changeSubscription(item) {
      this.modal.component = ChangeSubscription;
      this.itemToShow = item;
      this.modal.value = true;
    },
    tryAgain(item) {
      this.modal.component = TryAgain;
      this.itemToShow = item;
      this.modal.value = true;
    },
    openDetail(item) {
      this.$store.dispatch("sale/getSale", item.id).then(r => {
        this.$store.commit("sale/setSale", r);
        this.modal.value = true;
        this.modal.component = SaleDetail;
      });
    },

    submitRefund(id) {
      this.$store
        .dispatch("sale/refund", id)
        .then(() => {
          this.$message.success("O reembolso foi enviado com sucesso.");
          this.dispatch();
        })
        .catch(() => {
          this.$message.error("Erro ao enviar reembolso");
        });
    },
    cancelSubscription(id) {
      this.$store
        .dispatch("subscriptions/cancel", id)
        .then(() => {
          this.$message.success("Assinatura cancelada.");
          this.refresh();
        })
        .catch(() => {
          this.$message.error("Erro ao realizar cancelamento");
        });
    },
    rePix(item) {
      window.axios
        .post(`/payment/re-pix/${item.id}`)
        .then(() => {
          this.$message.success("Pix reenviado.");
        })
        .catch(() => {
          this.$message.error("Erro ao reenviar pix.");
        });
    },
    openReceipt(item) {
      window.open(
        `https://api.velora.com.br/v1/reports/${item.code}`,
        "_blank"
      );
    },

    allowRefund(item) {
      let refundable = ["paid", "completed"];
      return (
        moment().diff(moment(item.created_at), "day") <= 60 &&
        refundable.includes(item.status)
      );
    },
    isSmartCard(item) {
      return item.payment_method == "smart_card";
    },
    isAutoRenew(item) {
      return (
        item.charge_type === "signature" &&
        item.subscription &&
        item.subscription.cycles === "Renova até o cliente cancelar!"
      );
    },

    itemActions(item) {
      const actions = [];

      actions.push({
        label: "Detalhes",
        icon: "fa-light fa-info-circle",
        click: this.openDetail
      });

      if (
        this.$permissions.hasPermission("solicitar-reembolsos") &&
        this.allowRefund(item)
      ) {
        actions.push({
          label: "Reembolsar",
          class: "text-negative-600",
          icon: "fa-regular fa-money-bill-transfer",
          dialog: {
            text: `Deseja mesmo realizar o reembolso da transação: BP${item.id}?`,
            action: i => this.submitRefund(i.id),
            secondaryButtonText: "Cancelar"
          }
        });
      }

      if (item.billet) {
        actions.push({
          label: "Abrir boleto",
          icon: "icon-bar-code font-weight-bold",
          click: this.showBillet
        });
      } else if (item.payment_method === "pix" && item.status === "pending") {
        actions.push({
          label: "Reenviar pix",
          icon: "icon-pix",
          dialog: {
            text: "Deseja reenviar o pix?",
            buttonText: "Confirmar",
            secondaryButtonText: "Cancelar",
            action: this.rePix,
            icon: "fa fa-circle-exclamation"
          }
        });
      } else if (item.status === "failed") {
        actions.push({
          label: "Tentar novamente",
          icon: "icon-credit-card font-weight-bold",
          click: this.tryAgain
        });
      }

      actions.push({
        label: "Comprovante",
        icon: "fa-solid fa-receipt",
        click: this.openReceipt
      });

      if (item.charge_type === "signature" && item.status !== "failed") {
        if (this.$permissions.hasPermission("gerenciar-assinaturas")) {
          actions.push({
            label: "Configurar pagamento",
            icon: "icon-calendar font-weight-bold",
            click: i => this.changeSubscription(i.id)
          });
        }
        if (this.$permissions.hasPermission("cancelar-assinaturas")) {
          actions.push({
            label: "Cancelar assinatura",
            icon: "icon-x font-weight-bold",
            class: "text-negative-600",
            dialog: {
              text: `Deseja mesmo realizar o cancelamento da assinatura de ${item.shopper_name} no produto ${item.product_name}?`,
              variant: "negative",
              icon: "fa fa-hexagon-exclamation",
              secondaryAction: i => this.cancelSubscription(i.id),
              buttonText: "Cancelar",
              secondaryButtonText: "Confirmar"
            }
          });
        }
      }
      return actions;
    },

    closeModal() {
      this.modal.value = false;
    }
  },
  mounted() {
    this.$store.dispatch("integration/getCoproducerProducts", { limit: 1000 });
    this.dispatch();
  }
};
</script>
<style lang="scss">
.sales-base {
  .shopper-email {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
